import { supabase } from '../lib/supabase';
import { getPlainTextFromLexical, lexicalToHTML } from '../utils/lexicalUtils';
import { logger } from '../utils/logger';

/**
 * ============================================================================
 * STATUS MANAGEMENT FUNCTIONS
 * ============================================================================
 */

/**
 * Get all status types for a feature group
 */
export const getStatuses = async (featureGroupId) => {
  try {
    const { data, error } = await supabase
      .from('status')
      .select('*')
      .eq('feature_group_id', featureGroupId)
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error fetching statuses:', error);
    throw error;
  }
};

/**
 * Get a single status by ID
 */
export const getStatusById = async (statusId) => {
  try {
    const { data, error } = await supabase
      .from('status')
      .select('*')
      .eq('id', statusId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error fetching status:', error);
    throw error;
  }
};

/**
 * Create a new status type
 */
export const createStatus = async (statusData) => {
  try {
    const { data, error } = await supabase
      .from('status')
      .insert([{
        feature_group_id: statusData.feature_group_id,
        name: statusData.name.trim(),
        description: statusData.description || null,
        values: statusData.values // JSONB array of {value: number, label: string}
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error creating status:', error);
    throw error;
  }
};

/**
 * Update an existing status type
 */
export const updateStatus = async (statusId, statusData) => {
  try {
    const { data, error } = await supabase
      .from('status')
      .update({
        name: statusData.name.trim(),
        description: statusData.description || null,
        values: statusData.values,
        updated_at: new Date().toISOString()
      })
      .eq('id', statusId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error updating status:', error);
    throw error;
  }
};

/**
 * Delete a status type
 */
export const deleteStatus = async (statusId) => {
  try {
    const { error } = await supabase
      .from('status')
      .delete()
      .eq('id', statusId);

    if (error) throw error;
  } catch (error) {
    logger.error('Error deleting status:', error);
    throw error;
  }
};

/**
 * Get status values for a feature (all status types and their current values)
 */
export const getFeatureStatuses = async (featureId) => {
  try {
    const { data, error } = await supabase
      .from('status_feature')
      .select(`
        *,
        status (
          id,
          name,
          description,
          values
        )
      `)
      .eq('feature_id', featureId)
      .order('set_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error fetching feature statuses:', error);
    throw error;
  }
};

/**
 * Get status value for a specific feature and status type
 */
export const getFeatureStatus = async (featureId, statusId) => {
  try {
    const { data, error } = await supabase
      .from('status_feature')
      .select(`
        *,
        status (
          id,
          name,
          description,
          values
        )
      `)
      .eq('feature_id', featureId)
      .eq('status_id', statusId)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows
    return data || null;
  } catch (error) {
    logger.error('Error fetching feature status:', error);
    throw error;
  }
};

/**
 * Set or update a status value for a feature
 */
export const setFeatureStatus = async (featureId, statusId, value, userId) => {
  try {
    // Check if status already exists for this feature
    const { data: existing } = await supabase
      .from('status_feature')
      .select('id')
      .eq('feature_id', featureId)
      .eq('status_id', statusId)
      .maybeSingle();

    if (existing) {
      // Update existing status
      const { data, error } = await supabase
        .from('status_feature')
        .update({
          value,
          set_by: userId,
          set_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      // Create new status
      const { data, error } = await supabase
        .from('status_feature')
        .insert([{
          feature_id: featureId,
          status_id: statusId,
          value,
          set_by: userId,
          set_at: new Date().toISOString()
        }])
        .select()
        .single();

      if (error) throw error;
      return data;
    }
  } catch (error) {
    logger.error('Error setting feature status:', error);
    throw error;
  }
};

/**
 * Remove a status value from a feature
 */
export const removeFeatureStatus = async (featureId, statusId) => {
  try {
    const { error } = await supabase
      .from('status_feature')
      .delete()
      .eq('feature_id', featureId)
      .eq('status_id', statusId);

    if (error) throw error;
  } catch (error) {
    logger.error('Error removing feature status:', error);
    throw error;
  }
};

/**
 * Get the label for a status value
 */
export const getStatusValueLabel = (status, value) => {
  if (!status || !status.values || !Array.isArray(status.values)) {
    return 'Unknown';
  }
  
  const valueObj = status.values.find(v => v.value === value);
  return valueObj ? valueObj.label : 'Unknown';
};

/**
 * Fetch all features with their sub-features
 * Returns a hierarchical structure
 * @param {string|null} featureGroupId - The feature group ID
 * @param {string|null} userRole - The user's role ('Owner', 'Editor', 'Guest', or null)
 */
export const getAllFeatures = async (featureGroupId = null, userRole = null) => {
  try {
    let query = supabase
      .from('features')
      .select('*');

    if (featureGroupId) {
      query = query.eq('feature_group_id', featureGroupId);
    } else {
      query = query.is('feature_group_id', null);
    }

    const { data, error } = await query
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Filter out private features for Guests
    let filteredData = data;
    if (userRole === 'Guest') {
      filteredData = data.filter(feature => !feature.private_feature);
    }

    // Build hierarchical structure
    const featuresMap = new Map();
    const rootFeatures = [];

    // First pass: create all feature objects
    filteredData.forEach(feature => {
      featuresMap.set(feature.id, {
        ...feature,
        subFeatures: []
      });
    });

    // Second pass: build hierarchy (only include features that are in filteredData)
    filteredData.forEach(feature => {
      const featureObj = featuresMap.get(feature.id);
      if (feature.parent_id) {
        const parent = featuresMap.get(feature.parent_id);
        if (parent) {
          parent.subFeatures.push(featureObj);
        }
      } else {
        rootFeatures.push(featureObj);
      }
    });

    // Third pass: sort sub-features within each parent by sort_order, then created_at
    const sortSubFeatures = (feature) => {
      if (feature.subFeatures && feature.subFeatures.length > 0) {
        feature.subFeatures.sort((a, b) => {
          const aSort = a.sort_order || 0;
          const bSort = b.sort_order || 0;
          if (aSort !== bSort) {
            return aSort - bSort;
          }
          // Tiebreaker: use created_at
          const aDate = new Date(a.created_at || 0);
          const bDate = new Date(b.created_at || 0);
          return aDate - bDate;
        });
        // Recursively sort nested sub-features
        feature.subFeatures.forEach(subFeature => sortSubFeatures(subFeature));
      }
    };

    // Sort root features
    rootFeatures.sort((a, b) => {
      const aSort = a.sort_order || 0;
      const bSort = b.sort_order || 0;
      if (aSort !== bSort) {
        return aSort - bSort;
      }
      // Tiebreaker: use created_at
      const aDate = new Date(a.created_at || 0);
      const bDate = new Date(b.created_at || 0);
      return aDate - bDate;
    });

    // Sort all sub-features recursively
    rootFeatures.forEach(feature => sortSubFeatures(feature));

    return rootFeatures;
  } catch (error) {
    logger.error('Error fetching features:', error);
    throw error;
  }
};

/**
 * Fetch a single feature by ID
 */
export const getFeatureById = async (id) => {
  try {
    const { data, error } = await supabase
      .from('features')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error fetching feature:', error);
    throw error;
  }
};

/**
 * Get the most recently updated features across all of the user's feature maps
 * Limited to the specified number (default 20)
 */
export const getRecentFeaturesForUser = async (limit = 20) => {
  try {
    const {
      data: { user },
      error: authError,
    } = await supabase.auth.getUser();

    if (authError) throw authError;
    if (!user) throw new Error('User not authenticated');

    // Get all feature groups the user is a member of
    const { data: memberships, error: membershipsError } = await supabase
      .from('feature_group_members')
      .select('feature_group_id')
      .eq('user_id', user.id);

    if (membershipsError) throw membershipsError;

    const groupIds = (memberships || [])
      .map((m) => m.feature_group_id)
      .filter(Boolean);

    if (!groupIds.length) {
      return [];
    }

    // Get the most recently updated features across those groups
    // Include updated_by to show who updated each feature
    const { data, error } = await supabase
      .from('features')
      .select('id, name, feature_group_id, updated_at, updated_by')
      .in('feature_group_id', groupIds)
      .order('updated_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error fetching recent features for user:', error);
    throw error;
  }
};

/**
 * Create a new feature
 */
export const createFeature = async (featureData) => {
  try {
    // Use the provided sort_order if available, otherwise calculate max+1
    let newSortOrder = featureData.sort_order !== undefined ? featureData.sort_order : 0;
    
    if (featureData.sort_order === undefined) {
      // Get the max sort_order for siblings to place new feature at the end
      let siblingsQuery = supabase
        .from('features')
        .select('sort_order');

      // Handle parent_id correctly - use .is() for null values
      if (featureData.parent_id) {
        siblingsQuery = siblingsQuery.eq('parent_id', featureData.parent_id);
      } else {
        siblingsQuery = siblingsQuery.is('parent_id', null);
      }

      if (featureData.feature_group_id) {
        siblingsQuery = siblingsQuery.eq('feature_group_id', featureData.feature_group_id);
      } else {
        siblingsQuery = siblingsQuery.is('feature_group_id', null);
      }

      const { data: siblings, error: siblingsError } = await siblingsQuery
        .order('sort_order', { ascending: false })
        .limit(1);

      if (!siblingsError && siblings && siblings.length > 0) {
        newSortOrder = (siblings[0].sort_order || 0) + 1;
      }
    } else {
      // A specific sort_order was provided - need to shift existing features
      const targetParentId = featureData.parent_id || null;
      const targetFeatureGroupId = featureData.feature_group_id || null;

      // Get all siblings at the same level
      let siblingsQuery = supabase
        .from('features')
        .select('id, sort_order');

      if (targetParentId) {
        siblingsQuery = siblingsQuery.eq('parent_id', targetParentId);
      } else {
        siblingsQuery = siblingsQuery.is('parent_id', null);
      }

      if (targetFeatureGroupId) {
        siblingsQuery = siblingsQuery.eq('feature_group_id', targetFeatureGroupId);
      } else {
        siblingsQuery = siblingsQuery.is('feature_group_id', null);
      }

      const { data: siblings, error: siblingsError } = await siblingsQuery;

      if (siblingsError) throw siblingsError;

      // Shift all features with sort_order >= newSortOrder by incrementing by 1
      const featuresToShift = siblings.filter(s => {
        const sSort = s.sort_order || 0;
        return sSort >= newSortOrder;
      });

      // Update in reverse order to avoid conflicts
      featuresToShift.sort((a, b) => (b.sort_order || 0) - (a.sort_order || 0));

      for (const sibling of featuresToShift) {
        const newSort = (sibling.sort_order || 0) + 1;
        await supabase
          .from('features')
          .update({ sort_order: newSort })
          .eq('id', sibling.id);
      }
    }

    // Convert Lexical details to HTML
    const detailsHtml = featureData.details ? lexicalToHTML(featureData.details) : null;

    const { data, error } = await supabase
      .from('features')
      .insert([{
        name: featureData.name,
        description: featureData.description,
        details: featureData.details || null,
        details_html: detailsHtml,
        status: featureData.status || 'No Status',
        parent_id: featureData.parent_id || null,
        feature_group_id: featureData.feature_group_id || null,
        sort_order: newSortOrder,
        image_url: featureData.image_url || null,
        ai_prompt: featureData.ai_prompt || null,
        private_feature: featureData.private_feature || false
      }])
      .select()
      .single();

    if (error) throw error;

    // If feature is private, cascade to all children
    if (data && data.private_feature) {
      await cascadePrivateToChildren(data.id, data.feature_group_id, true);
    }

    return data;
  } catch (error) {
    logger.error('Error creating feature:', error);
    throw error;
  }
};

/**
 * Update an existing feature
 */
export const updateFeature = async (id, featureData) => {
  try {
    // Get the current feature to check old sort_order and updated_at for optimistic locking
    const { data: currentFeature, error: currentError } = await supabase
      .from('features')
      .select('sort_order, parent_id, feature_group_id, updated_at')
      .eq('id', id)
      .single();

    if (currentError) throw currentError;
    
    // Optimistic locking: Check if feature was modified since it was loaded
    if (featureData.originalUpdatedAt && currentFeature.updated_at !== featureData.originalUpdatedAt) {
      const error = new Error('FEATURE_CONFLICT');
      error.message = 'This feature was modified by another user. Please reload and try again.';
      error.originalUpdatedAt = featureData.originalUpdatedAt;
      error.currentUpdatedAt = currentFeature.updated_at;
      throw error;
    }

    // Convert Lexical details to HTML if details are being updated
    let detailsHtml = undefined;
    if (featureData.details !== undefined) {
      detailsHtml = featureData.details ? lexicalToHTML(featureData.details) : null;
    }

    const updateData = {
      name: featureData.name,
      description: featureData.description,
      details: featureData.details !== undefined ? (featureData.details || null) : undefined,
      details_html: detailsHtml,
      parent_id: featureData.parent_id !== undefined ? (featureData.parent_id || null) : undefined,
      updated_at: new Date().toISOString()
    };

    // Add optional fields if they are provided
    if (featureData.status !== undefined) {
      updateData.status = featureData.status || 'No Status';
    }
    if (featureData.image_url !== undefined) {
      updateData.image_url = featureData.image_url || null;
    }
    if (featureData.ai_prompt !== undefined) {
      updateData.ai_prompt = featureData.ai_prompt || null;
    }
    if (featureData.private_feature !== undefined) {
      updateData.private_feature = featureData.private_feature || false;
    }

    // Determine old and new parent IDs
    const oldParentId = currentFeature.parent_id || null;
    const newParentId = featureData.parent_id !== undefined 
      ? (featureData.parent_id || null)
      : oldParentId;
    const parentChanged = oldParentId !== newParentId;
    const targetFeatureGroupId = featureData.feature_group_id || currentFeature.feature_group_id;
    
    // Ensure feature_group_id is preserved (don't allow changing it via update)
    // If parent changed, verify parent belongs to same feature group
    if (newParentId) {
      const { data: parentFeature } = await supabase
        .from('features')
        .select('feature_group_id')
        .eq('id', newParentId)
        .single();
      
      if (parentFeature && parentFeature.feature_group_id !== targetFeatureGroupId) {
        throw new Error('Cannot set parent to a feature from a different feature group');
      }
    }
    
    // Preserve the feature_group_id (don't allow it to be changed via update)
    updateData.feature_group_id = targetFeatureGroupId;

    // Handle sort_order update with atomic function for race condition prevention
    if (featureData.sort_order !== undefined && featureData.sort_order !== null) {
      const newSortOrder = parseInt(featureData.sort_order);
      const oldSortOrder = currentFeature.sort_order || 0;

      // Use atomic RPC function if parent or sort_order changed
      if (parentChanged || newSortOrder !== oldSortOrder) {
        // Call atomic function to handle all sort_order shifts in a single transaction
        const { error: atomicError } = await supabase.rpc('update_feature_sort_order_atomic', {
          p_feature_id: id,
          p_old_parent_id: oldParentId,
          p_new_parent_id: newParentId,
          p_old_sort_order: oldSortOrder,
          p_new_sort_order: newSortOrder,
          p_feature_group_id: targetFeatureGroupId
        });

        if (atomicError) {
          logger.error('Error in atomic sort_order update:', atomicError);
          throw atomicError;
        }

        // Update the feature's parent_id and sort_order (atomic function already did this, but we need to include in updateData for consistency)
        updateData.parent_id = newParentId;
        updateData.sort_order = newSortOrder;
      } else {
        // No change to parent or sort_order, just update other fields
        updateData.sort_order = newSortOrder;
      }
    } else if (parentChanged) {
      // Parent changed but no sort_order specified - assign max+1 at new parent
      let newSiblingsQuery = supabase
        .from('features')
        .select('sort_order');

      if (newParentId) {
        newSiblingsQuery = newSiblingsQuery.eq('parent_id', newParentId);
      } else {
        newSiblingsQuery = newSiblingsQuery.is('parent_id', null);
      }

      if (targetFeatureGroupId) {
        newSiblingsQuery = newSiblingsQuery.eq('feature_group_id', targetFeatureGroupId);
      } else {
        newSiblingsQuery = newSiblingsQuery.is('feature_group_id', null);
      }

      const { data: newSiblings, error: newSiblingsError } = await newSiblingsQuery
        .order('sort_order', { ascending: false })
        .limit(1);

      if (newSiblingsError) throw newSiblingsError;

      let newSortOrder = 0;
      if (newSiblings && newSiblings.length > 0) {
        newSortOrder = (newSiblings[0].sort_order || 0) + 1;
      }

      updateData.sort_order = newSortOrder;

      // Also need to shift old parent siblings to fill the gap
      const oldSortOrder = currentFeature.sort_order || 0;
      let oldSiblingsQuery = supabase
        .from('features')
        .select('id, sort_order');

      if (oldParentId) {
        oldSiblingsQuery = oldSiblingsQuery.eq('parent_id', oldParentId);
      } else {
        oldSiblingsQuery = oldSiblingsQuery.is('parent_id', null);
      }

      if (targetFeatureGroupId) {
        oldSiblingsQuery = oldSiblingsQuery.eq('feature_group_id', targetFeatureGroupId);
      } else {
        oldSiblingsQuery = oldSiblingsQuery.is('feature_group_id', null);
      }

      const { data: oldSiblings, error: oldSiblingsError } = await oldSiblingsQuery;
      if (oldSiblingsError) throw oldSiblingsError;

      const oldSiblingsToShift = oldSiblings.filter(s => {
        return s.id !== id && (s.sort_order || 0) > oldSortOrder;
      });

      for (const sibling of oldSiblingsToShift) {
        const newSort = (sibling.sort_order || 0) - 1;
        await supabase
          .from('features')
          .update({ sort_order: newSort })
          .eq('id', sibling.id);
      }
    }

    // Update the feature (sort_order was already handled by atomic function if needed)
    // Remove sort_order from updateData if it was handled atomically
    const finalUpdateData = { ...updateData };
    if (featureData.sort_order !== undefined && featureData.sort_order !== null) {
      // Sort order was handled by atomic function, don't include it in the update
      delete finalUpdateData.sort_order;
      delete finalUpdateData.parent_id; // Also handled by atomic function
    }
    
    const { data, error } = await supabase
      .from('features')
      .update(finalUpdateData)
      .eq('id', id)
      .select()
      .single();
    
    if (error) throw error;

    // If private_feature was updated, cascade to all children
    if (featureData.private_feature !== undefined && data) {
      await cascadePrivateToChildren(data.id, data.feature_group_id, featureData.private_feature);
    }

    return data;
  } catch (error) {
    logger.error('Error updating feature:', error);
    throw error;
  }
};

/**
 * Cascade private_feature status to all children recursively
 */
const cascadePrivateToChildren = async (parentId, featureGroupId, isPrivate) => {
  try {
    // Get all direct children
    let childrenQuery = supabase
      .from('features')
      .select('id')
      .eq('parent_id', parentId);

    if (featureGroupId) {
      childrenQuery = childrenQuery.eq('feature_group_id', featureGroupId);
    } else {
      childrenQuery = childrenQuery.is('feature_group_id', null);
    }

    const { data: children, error } = await childrenQuery;

    if (error) throw error;

    if (!children || children.length === 0) return;

    // Update all children to have the same private_feature status
    const childIds = children.map(c => c.id);
    const { error: updateError } = await supabase
      .from('features')
      .update({ private_feature: isPrivate })
      .in('id', childIds);

    if (updateError) throw updateError;

    // Recursively cascade to grandchildren
    for (const child of children) {
      await cascadePrivateToChildren(child.id, featureGroupId, isPrivate);
    }
  } catch (error) {
    logger.error('Error cascading private status to children:', error);
    throw error;
  }
};

/**
 * Delete a feature (cascade will delete sub-features)
 */
export const deleteFeature = async (id) => {
  try {
    const { error } = await supabase
      .from('features')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    logger.error('Error deleting feature:', error);
    throw error;
  }
};

/**
 * Move a feature up or down in the list
 * @param {string} featureId - ID of the feature to move
 * @param {string} direction - 'up' or 'down'
 */
export const moveFeature = async (featureId, direction) => {
  try {
    // Get the feature to move
    const { data: currentFeature, error: currentError } = await supabase
      .from('features')
      .select('*')
      .eq('id', featureId)
      .single();

    if (currentError) throw currentError;

    // Get all siblings (features with the same parent_id and feature_group_id)
    let siblingsQuery = supabase
      .from('features')
      .select('*');

    if (currentFeature.parent_id) {
      siblingsQuery = siblingsQuery.eq('parent_id', currentFeature.parent_id);
    } else {
      siblingsQuery = siblingsQuery.is('parent_id', null);
    }

    if (currentFeature.feature_group_id) {
      siblingsQuery = siblingsQuery.eq('feature_group_id', currentFeature.feature_group_id);
    } else {
      siblingsQuery = siblingsQuery.is('feature_group_id', null);
    }

    const { data: siblings, error: siblingsError } = await siblingsQuery
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (siblingsError) throw siblingsError;

    // Find current index
    const currentIndex = siblings.findIndex(f => f.id === featureId);

    if (currentIndex === -1) {
      throw new Error('Feature not found in siblings');
    }

    // Determine target index
    let targetIndex;
    if (direction === 'up' && currentIndex > 0) {
      targetIndex = currentIndex - 1;
    } else if (direction === 'down' && currentIndex < siblings.length - 1) {
      targetIndex = currentIndex + 1;
    } else {
      // Already at the top or bottom
      return;
    }

    const targetFeature = siblings[targetIndex];
    const currentSortOrder = currentFeature.sort_order || 0;
    const targetSortOrder = targetFeature.sort_order || 0;

    // Check if target feature has children
    const checkHasChildren = async (parentId, featureGroupId) => {
      let childrenQuery = supabase
        .from('features')
        .select('id, sort_order');

      if (parentId) {
        childrenQuery = childrenQuery.eq('parent_id', parentId);
      } else {
        childrenQuery = childrenQuery.is('parent_id', null);
      }

      if (featureGroupId) {
        childrenQuery = childrenQuery.eq('feature_group_id', featureGroupId);
      } else {
        childrenQuery = childrenQuery.is('feature_group_id', null);
      }

      const { data: children, error } = await childrenQuery
        .order('sort_order', { ascending: false })
        .limit(1);

      if (error) throw error;
      return children && children.length > 0;
    };

    const getMaxChildSortOrder = async (parentId, featureGroupId) => {
      // Get all children recursively
      const getAllChildren = async (parentId) => {
        let childrenQuery = supabase
          .from('features')
          .select('id, sort_order');

        if (parentId) {
          childrenQuery = childrenQuery.eq('parent_id', parentId);
        } else {
          childrenQuery = childrenQuery.is('parent_id', null);
        }

        if (featureGroupId) {
          childrenQuery = childrenQuery.eq('feature_group_id', featureGroupId);
        } else {
          childrenQuery = childrenQuery.is('feature_group_id', null);
        }

        const { data: directChildren, error } = await childrenQuery;
        if (error) throw error;

        let allChildren = [...directChildren];
        
        // Recursively get grandchildren
        for (const child of directChildren) {
          const grandchildren = await getAllChildren(child.id);
          allChildren = allChildren.concat(grandchildren);
        }

        return allChildren;
      };

      const allChildren = await getAllChildren(parentId);
      if (allChildren.length === 0) return null;

      // Find max sort_order among all children
      const maxSortOrder = Math.max(...allChildren.map(c => c.sort_order || 0));
      return maxSortOrder;
    };

    const hasChildren = await checkHasChildren(targetFeature.id, currentFeature.feature_group_id);

    if (hasChildren) {
      if (direction === 'down') {
        // Moving down past a feature with children: place after all children
        const maxChildSortOrder = await getMaxChildSortOrder(targetFeature.id, currentFeature.feature_group_id);
        const newSortOrder = maxChildSortOrder !== null ? maxChildSortOrder + 1 : targetSortOrder + 1;

        // Update the moved feature's sort_order
        const { error: updateError } = await supabase
          .from('features')
          .update({ sort_order: newSortOrder })
          .eq('id', featureId);

        if (updateError) throw updateError;
      } else {
        // Moving up past a feature with children: place before the parent (swap with target)
        const { error: updateCurrentError } = await supabase
          .from('features')
          .update({ sort_order: targetSortOrder })
          .eq('id', featureId);

        if (updateCurrentError) throw updateCurrentError;

        const { error: updateTargetError } = await supabase
          .from('features')
          .update({ sort_order: currentSortOrder })
          .eq('id', targetFeature.id);

        if (updateTargetError) throw updateTargetError;
      }
    } else {
      // Standard swap when target has no children
      const { error: updateCurrentError } = await supabase
        .from('features')
        .update({ sort_order: targetSortOrder })
        .eq('id', featureId);

      if (updateCurrentError) throw updateCurrentError;

      const { error: updateTargetError } = await supabase
        .from('features')
        .update({ sort_order: currentSortOrder })
        .eq('id', targetFeature.id);

      if (updateTargetError) throw updateTargetError;
    }

    // After move operation, check for and fix any duplicate sort_order values
    const fixDuplicateSortOrders = async (parentId, featureGroupId) => {
      // Get all siblings at this level
      let siblingsQuery = supabase
        .from('features')
        .select('id, sort_order, created_at');

      if (parentId) {
        siblingsQuery = siblingsQuery.eq('parent_id', parentId);
      } else {
        siblingsQuery = siblingsQuery.is('parent_id', null);
      }

      if (featureGroupId) {
        siblingsQuery = siblingsQuery.eq('feature_group_id', featureGroupId);
      } else {
        siblingsQuery = siblingsQuery.is('feature_group_id', null);
      }

      const { data: allSiblings, error: siblingsError } = await siblingsQuery
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: true });

      if (siblingsError) throw siblingsError;

      if (!allSiblings || allSiblings.length === 0) return;

      // Check for duplicates
      const sortOrderSet = new Set();
      let hasDuplicates = false;

      for (const sibling of allSiblings) {
        const sortOrder = sibling.sort_order || 0;
        if (sortOrderSet.has(sortOrder)) {
          hasDuplicates = true;
          break;
        }
        sortOrderSet.add(sortOrder);
      }

      // Check if sort_order values are sequential (0, 1, 2, ...)
      let needsReordering = false;
      if (!hasDuplicates) {
        for (let i = 0; i < allSiblings.length; i++) {
          const expectedSortOrder = i;
          const actualSortOrder = allSiblings[i].sort_order || 0;
          if (actualSortOrder !== expectedSortOrder) {
            needsReordering = true;
            break;
          }
        }
      }

      // If duplicates found or not sequential, reassign sequential sort_order values
      if (hasDuplicates || needsReordering) {
        // Reassign all siblings with sequential sort_order based on current order
        for (let i = 0; i < allSiblings.length; i++) {
          const sibling = allSiblings[i];
          const expectedSortOrder = i;
          
          if ((sibling.sort_order || 0) !== expectedSortOrder) {
            await supabase
              .from('features')
              .update({ sort_order: expectedSortOrder })
              .eq('id', sibling.id);
          }
        }
      }
    };

    // Fix duplicates for the current parent level
    await fixDuplicateSortOrders(currentFeature.parent_id, currentFeature.feature_group_id);
  } catch (error) {
    logger.error('Error moving feature:', error);
    throw error;
  }
};

/**
 * Search/filter features by name or description
 * Returns hierarchical structure with matching features and their parents
 */
export const searchFeatures = async (searchTerm, featureGroupId = null) => {
  try {
    const searchLower = searchTerm.toLowerCase();
    
    // Get all features to build full hierarchy
    let query = supabase
      .from('features')
      .select('*');

    if (featureGroupId) {
      query = query.eq('feature_group_id', featureGroupId);
    } else {
      query = query.is('feature_group_id', null);
    }

    const { data: allFeatures, error } = await query
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });

    if (error) throw error;

    // Find matching feature IDs (including checking sub-features)
    const matchesSearch = (feature) => {
      const nameMatch = feature.name.toLowerCase().includes(searchLower);
      
      // Description is now plain text
      const descMatch = feature.description 
        ? feature.description.toLowerCase().includes(searchLower)
        : false;
      
      // Extract plain text from Lexical JSON for details field
      let detailsText = '';
      if (feature.details) {
        try {
          const parsed = typeof feature.details === 'string' ? JSON.parse(feature.details) : feature.details;
          if (parsed && parsed.root) {
            detailsText = getPlainTextFromLexical(feature.details);
          }
        } catch (e) {
          // If it's not JSON, use as plain text
          detailsText = typeof feature.details === 'string' ? feature.details : '';
        }
      }
      
      const detailsMatch = detailsText.toLowerCase().includes(searchLower);
      
      return nameMatch || descMatch || detailsMatch;
    };

    const matchedIds = new Set();
    const allFeaturesMap = new Map();

    // Build map and identify matches
    allFeatures.forEach(feature => {
      allFeaturesMap.set(feature.id, { ...feature, subFeatures: [] });
      if (matchesSearch(feature)) {
        matchedIds.add(feature.id);
      }
    });

    // Build hierarchy
    const rootFeatures = [];
    allFeatures.forEach(feature => {
      const featureObj = allFeaturesMap.get(feature.id);
      if (feature.parent_id) {
        const parent = allFeaturesMap.get(feature.parent_id);
        if (parent) {
          parent.subFeatures.push(featureObj);
        }
      } else {
        rootFeatures.push(featureObj);
      }
    });

    // Filter to show only matching features and their parents/children
    const shouldInclude = (feature) => {
      if (matchedIds.has(feature.id)) return true;
      if (feature.subFeatures.some(sub => shouldInclude(sub))) return true;
      return false;
    };

    const filterTree = (features) => {
      return features
        .filter(shouldInclude)
        .map(feature => ({
          ...feature,
          subFeatures: filterTree(feature.subFeatures)
        }));
    };

    return filterTree(rootFeatures);
  } catch (error) {
    logger.error('Error searching features:', error);
    throw error;
  }
};

/**
 * Get all links for a feature
 */
export const getFeatureLinks = async (featureId) => {
  try {
    const { data, error } = await supabase
      .from('feature_links')
      .select('*')
      .eq('feature_id', featureId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error fetching feature links:', error);
    throw error;
  }
};

/**
 * Get link counts by type for a feature
 */
export const getFeatureLinkCounts = async (featureId) => {
  try {
    const { data, error } = await supabase
      .from('feature_links')
      .select('type')
      .eq('feature_id', featureId);

    if (error) throw error;

    const counts = {
      'Internal': 0,
      'Customer Docs': 0,
      'Dev Docs': 0,
      'Ideas': 0,
      'Initiatives': 0
    };

    (data || []).forEach(link => {
      if (counts.hasOwnProperty(link.type)) {
        counts[link.type]++;
      }
    });

    return counts;
  } catch (error) {
    logger.error('Error fetching feature link counts:', error);
    throw error;
  }
};

/**
 * Get link counts by type for multiple features
 */
export const getFeatureLinkCountsBatch = async (featureIds) => {
  try {
    if (!featureIds || featureIds.length === 0) {
      return new Map();
    }

    const { data, error } = await supabase
      .from('feature_links')
      .select('feature_id, type')
      .in('feature_id', featureIds);

    if (error) throw error;

    const countsMap = new Map();
    featureIds.forEach(id => {
      countsMap.set(id, {
        'Internal': 0,
        'Customer Docs': 0,
        'Dev Docs': 0,
        'Ideas': 0,
        'Initiatives': 0
      });
    });

    (data || []).forEach(link => {
      const counts = countsMap.get(link.feature_id);
      if (counts && counts.hasOwnProperty(link.type)) {
        counts[link.type]++;
      }
    });

    return countsMap;
  } catch (error) {
    logger.error('Error fetching feature link counts batch:', error);
    throw error;
  }
};

/**
 * Create a new feature link
 */
export const createFeatureLink = async (linkData) => {
  try {
    const { data, error } = await supabase
      .from('feature_links')
      .insert([{
        feature_id: linkData.feature_id,
        url: linkData.url,
        name: linkData.name,
        type: linkData.type
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error creating feature link:', error);
    throw error;
  }
};

/**
 * Update an existing feature link
 */
export const updateFeatureLink = async (id, linkData) => {
  try {
    const { data, error } = await supabase
      .from('feature_links')
      .update({
        url: linkData.url,
        name: linkData.name,
        type: linkData.type,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error updating feature link:', error);
    throw error;
  }
};

/**
 * Delete a feature link
 */
export const deleteFeatureLink = async (id) => {
  try {
    const { error } = await supabase
      .from('feature_links')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    logger.error('Error deleting feature link:', error);
    throw error;
  }
};

// ==========================================
// Feature-to-Feature Links (Bidirectional)
// ==========================================

/**
 * Get all feature-to-feature links for a feature (both directions)
 * Returns links where the feature is either source or target
 */
export const getFeatureFeatureLinks = async (featureId) => {
  try {
    // Get links where this feature is the source
    const { data: sourceLinks, error: sourceError } = await supabase
      .from('feature_feature_links')
      .select(`
        id,
        source_feature_id,
        target_feature_id,
        link_type,
        created_at,
        target_feature:features!feature_feature_links_target_feature_id_fkey (
          id,
          name,
          description,
          status
        )
      `)
      .eq('source_feature_id', featureId);

    if (sourceError) throw sourceError;

    // Get links where this feature is the target (reverse direction)
    const { data: targetLinks, error: targetError } = await supabase
      .from('feature_feature_links')
      .select(`
        id,
        source_feature_id,
        target_feature_id,
        link_type,
        created_at,
        source_feature:features!feature_feature_links_source_feature_id_fkey (
          id,
          name,
          description,
          status
        )
      `)
      .eq('target_feature_id', featureId);

    if (targetError) throw targetError;

    // Transform source links (outgoing)
    const outgoingLinks = (sourceLinks || []).map(link => ({
      id: link.id,
      linkedFeatureId: link.target_feature_id,
      linkedFeature: link.target_feature,
      linkType: link.link_type,
      direction: 'outgoing',
      created_at: link.created_at
    }));

    // Transform target links (incoming) - reverse the link type display
    const incomingLinks = (targetLinks || []).map(link => ({
      id: link.id,
      linkedFeatureId: link.source_feature_id,
      linkedFeature: link.source_feature,
      linkType: getReverseLinkType(link.link_type),
      direction: 'incoming',
      originalLinkType: link.link_type,
      created_at: link.created_at
    }));

    return [...outgoingLinks, ...incomingLinks];
  } catch (error) {
    logger.error('Error fetching feature-to-feature links:', error);
    throw error;
  }
};

/**
 * Get the reverse link type for bidirectional display
 */
const getReverseLinkType = (linkType) => {
  switch (linkType) {
    case 'depends_on':
      return 'depended_on_by';
    case 'depended_on_by':
      return 'depends_on';
    case 'related_to':
      return 'related_to'; // Symmetric
    default:
      return linkType;
  }
};

/**
 * Create a feature-to-feature link
 */
export const createFeatureFeatureLink = async (sourceFeatureId, targetFeatureId, linkType) => {
  try {
    const { data, error } = await supabase
      .from('feature_feature_links')
      .insert([{
        source_feature_id: sourceFeatureId,
        target_feature_id: targetFeatureId,
        link_type: linkType
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error creating feature-to-feature link:', error);
    throw error;
  }
};

/**
 * Delete a feature-to-feature link
 */
export const deleteFeatureFeatureLink = async (id) => {
  try {
    const { error } = await supabase
      .from('feature_feature_links')
      .delete()
      .eq('id', id);

    if (error) throw error;
  } catch (error) {
    logger.error('Error deleting feature-to-feature link:', error);
    throw error;
  }
};

/**
 * Search features by name for typeahead (excludes the current feature)
 */
export const searchFeaturesForLinking = async (query, featureGroupId, excludeFeatureId) => {
  try {
    let queryBuilder = supabase
      .from('features')
      .select('id, name, description, status')
      .eq('feature_group_id', featureGroupId)
      .ilike('name', `%${query}%`)
      .limit(10);

    if (excludeFeatureId) {
      queryBuilder = queryBuilder.neq('id', excludeFeatureId);
    }

    const { data, error } = await queryBuilder;

    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error searching features for linking:', error);
    throw error;
  }
};

// ==========================================
// System Settings
// ==========================================

/**
 * Get a system setting value
 */
export const getSystemSetting = async (settingKey) => {
  try {
    const { data, error } = await supabase
      .from('system_settings')
      .select('setting_value')
      .eq('setting_key', settingKey)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 = no rows returned
    return data?.setting_value || null;
  } catch (error) {
    logger.error('Error fetching system setting:', error);
    throw error;
  }
};

/**
 * Set a system setting value (superuser only)
 */
export const setSystemSetting = async (settingKey, settingValue) => {
  try {
    const { data, error } = await supabase
      .from('system_settings')
      .upsert({
        setting_key: settingKey,
        setting_value: settingValue,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'setting_key'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error setting system setting:', error);
    throw error;
  }
};

/**
 * Get all feature groups the current user is a member of with stats
 * Includes both direct memberships (feature_group_members) and company-based access (user_companies)
 */
export const getUserFeatureGroups = async () => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('User not authenticated');

    // Get direct memberships (including cached counts)
    const { data: membersData, error: membersError } = await supabase
      .from('feature_group_members')
      .select(`
        feature_group_id,
        role,
        feature_groups (
          id,
          display_name,
          description,
          created_at,
          updated_at,
          cached_feature_count,
          cached_user_count,
          cached_dimensions_count
        )
      `)
      .eq('user_id', user.id);

    if (membersError) throw membersError;

    // Get company-based access (feature groups user has access to through user_companies)
    const { data: companyData, error: companyError } = await supabase
      .from('user_companies')
      .select(`
        feature_group_id,
        feature_groups (
          id,
          display_name,
          description,
          created_at,
          updated_at,
          cached_feature_count,
          cached_user_count,
          cached_dimensions_count
        )
      `)
      .eq('user_id', user.id);

    if (companyError) throw companyError;

    // Combine both sources, using a Map to avoid duplicates
    // Direct memberships take precedence (if user is both a member and has company access)
    const groupMap = new Map();

    // Add direct memberships first
    (membersData || []).forEach((member) => {
      if (member.feature_groups) {
        groupMap.set(member.feature_groups.id, {
          id: member.feature_groups.id,
          display_name: member.feature_groups.display_name,
          description: member.feature_groups.description || null,
          role: member.role,
          created_at: member.feature_groups.created_at,
          updated_at: member.feature_groups.updated_at,
          cached_feature_count: member.feature_groups.cached_feature_count,
          cached_user_count: member.feature_groups.cached_user_count,
          cached_dimensions_count: member.feature_groups.cached_dimensions_count
        });
      }
    });

    // Add company-based access (only if not already in map)
    (companyData || []).forEach((uc) => {
      if (uc.feature_groups && !groupMap.has(uc.feature_groups.id)) {
        groupMap.set(uc.feature_groups.id, {
          id: uc.feature_groups.id,
          display_name: uc.feature_groups.display_name,
          description: uc.feature_groups.description || null,
          role: 'Guest', // Company-based access gets Guest role
          created_at: uc.feature_groups.created_at,
          updated_at: uc.feature_groups.updated_at,
          cached_feature_count: uc.feature_groups.cached_feature_count,
          cached_user_count: uc.feature_groups.cached_user_count,
          cached_dimensions_count: uc.feature_groups.cached_dimensions_count
        });
      }
    });

    // Convert map to array and sort by created_at
    const allGroups = Array.from(groupMap.values()).sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );

    // Use cached counts from database (automatically maintained by triggers)
    // No need to make additional queries - counts are already in the group objects
    const groupsWithStats = allGroups.map((group) => {
      return {
        id: group.id,
        display_name: group.display_name,
        description: group.description || null,
        role: group.role,
        created_at: group.created_at,
        updated_at: group.updated_at,
        feature_count: group.cached_feature_count ?? 0,
        user_count: group.cached_user_count ?? 0,
        dimensions_count: group.cached_dimensions_count ?? 0
      };
    });

    return groupsWithStats;
  } catch (error) {
    logger.error('Error fetching user feature groups:', error);
    throw error;
  }
};

/**
 * Get a feature group by ID
 */
export const getFeatureGroup = async (groupId) => {
  try {
    const { data, error } = await supabase
      .from('feature_groups')
      .select('*')
      .eq('id', groupId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error fetching feature group:', error);
    throw error;
  }
};

/**
 * Update a feature group's display name and description (only for owners)
 */
export const updateFeatureGroup = async (groupId, displayName, description = null) => {
  try {
    const updateData = {
      display_name: displayName.trim(),
      updated_at: new Date().toISOString()
    };
    
    if (description !== undefined) {
      updateData.description = description?.trim() || null;
    }
    
    const { data, error } = await supabase
      .from('feature_groups')
      .update(updateData)
      .eq('id', groupId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error updating feature group:', error);
    throw error;
  }
};

/**
 * Get user's role in a feature group
 */
export const getUserRoleInFeatureGroup = async (groupId) => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data, error } = await supabase
      .from('feature_group_members')
      .select('role')
      .eq('feature_group_id', groupId)
      .eq('user_id', user.id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // No rows returned
      throw error;
    }

    // Update last_visit when user accesses the feature group
    await updateLastVisit(groupId, user.id);

    return data?.role || null;
  } catch (error) {
    logger.error('Error fetching user role:', error);
    throw error;
  }
};

/**
 * Update last_visit timestamp for a user in a feature group
 */
export const updateLastVisit = async (featureGroupId, userId) => {
  try {
    const { error } = await supabase
      .from('feature_group_members')
      .update({ last_visit: new Date().toISOString() })
      .eq('feature_group_id', featureGroupId)
      .eq('user_id', userId);

    if (error) {
      // Don't throw error - this is a non-critical update
      logger.warn('Error updating last_visit:', error);
    }
  } catch (error) {
    // Don't throw error - this is a non-critical update
    logger.warn('Error updating last_visit:', error);
  }
};

/**
 * Get all tags for a feature group (now organized by dimension)
 */
export const getTags = async (featureGroupId, filter = '') => {
  try {
    let query = supabase
      .from('tags')
      .select(`
        *,
        dimensions (
          id,
          name,
          dimension_type
        )
      `)
      .eq('feature_group_id', featureGroupId)
      .order('name', { ascending: true });

    if (filter.trim()) {
      const filterLower = filter.toLowerCase();
      // Filter by name (case-insensitive)
      query = query.ilike('name', `%${filterLower}%`);
    }

    const { data, error } = await query;
    if (error) throw error;
    
    // If filter is provided, also filter by keywords in JavaScript
    if (filter.trim() && data) {
      const filterLower = filter.toLowerCase();
      return data.filter(tag => {
        const nameMatch = tag.name.toLowerCase().includes(filterLower);
        const keywordMatch = tag.keywords && tag.keywords.some(kw => 
          kw.toLowerCase().includes(filterLower)
        );
        return nameMatch || keywordMatch;
      });
    }
    
    return data || [];
  } catch (error) {
    logger.error('Error fetching tags:', error);
    throw error;
  }
};

/**
 * Get tags for a specific dimension
 */
export const getTagsByDimension = async (dimensionId, filter = '') => {
  try {
    let query = supabase
      .from('tags')
      .select('*')
      .eq('dimension_id', dimensionId)
      .order('name', { ascending: true });

    if (filter.trim()) {
      const filterLower = filter.toLowerCase();
      query = query.ilike('name', `%${filterLower}%`);
    }

    const { data, error } = await query;
    if (error) throw error;
    
    return data || [];
  } catch (error) {
    logger.error('Error fetching tags by dimension:', error);
    throw error;
  }
};

/**
 * Get a single tag by ID
 */
export const getTagById = async (tagId) => {
  try {
    const { data, error } = await supabase
      .from('tags')
      .select('*')
      .eq('id', tagId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error fetching tag:', error);
    throw error;
  }
};

/**
 * Create a new tag (now requires dimension_id instead of type)
 */
export const createTag = async (tagData) => {
  try {
    // Get dimension to find feature_group_id
    const dimension = await getDimensionById(tagData.dimension_id);
    
    const { data, error } = await supabase
      .from('tags')
      .insert([{
        dimension_id: tagData.dimension_id,
        feature_group_id: dimension.feature_group_id,
        name: tagData.name.trim(),
        keywords: tagData.keywords || []
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error creating tag:', error);
    throw error;
  }
};

/**
 * Update an existing tag
 */
export const updateTag = async (tagId, tagData) => {
  try {
    const { data, error } = await supabase
      .from('tags')
      .update({
        name: tagData.name?.trim(),
        keywords: tagData.keywords || [],
        updated_at: new Date().toISOString()
      })
      .eq('id', tagId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error updating tag:', error);
    throw error;
  }
};

/**
 * Delete a tag
 */
export const deleteTag = async (tagId) => {
  try {
    const { error } = await supabase
      .from('tags')
      .delete()
      .eq('id', tagId);

    if (error) throw error;
  } catch (error) {
    logger.error('Error deleting tag:', error);
    throw error;
  }
};

/**
 * Get feature counts for multiple tags (batch query)
 * Returns a Map of tagId -> count
 */
export const getTagFeatureCounts = async (tagIds) => {
  try {
    if (!tagIds || tagIds.length === 0) {
      return new Map();
    }

    const { data, error } = await supabase
      .from('feature_tags')
      .select('tag_id')
      .in('tag_id', tagIds);

    if (error) throw error;

    // Count occurrences of each tag_id
    const counts = new Map();
    tagIds.forEach(tagId => {
      counts.set(tagId, 0);
    });

    if (data) {
      data.forEach(ft => {
        const currentCount = counts.get(ft.tag_id) || 0;
        counts.set(ft.tag_id, currentCount + 1);
      });
    }

    return counts;
  } catch (error) {
    logger.error('Error fetching tag feature counts:', error);
    throw error;
  }
};

/**
 * Get features associated with a tag
 */
export const getFeaturesByTag = async (tagId) => {
  try {
    const { data, error } = await supabase
      .from('feature_tags')
      .select(`
        feature_id,
        features (
          id,
          name,
          description,
          feature_group_id
        )
      `)
      .eq('tag_id', tagId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(ft => ft.features).filter(Boolean);
  } catch (error) {
    logger.error('Error fetching features by tag:', error);
    throw error;
  }
};

/**
 * Get tags for a feature (with dimension info)
 */
export const getFeatureTags = async (featureId) => {
  try {
    const { data, error } = await supabase
      .from('feature_tags')
      .select(`
        tag_id,
        state,
        rating,
        comment,
        tags (
          id,
          name,
          keywords,
          dimension_id,
          dimensions (
            id,
            name,
            dimension_type,
            user_directions
          )
        )
      `)
      .eq('feature_id', featureId);

    if (error) throw error;
    return (data || []).map(ft => ({
      ...ft.tags,
      dimension: ft.tags?.dimensions,
      state: ft.state,
      rating: ft.rating,
      comment: ft.comment
    })).filter(Boolean);
  } catch (error) {
    logger.error('Error fetching feature tags:', error);
    throw error;
  }
};

/**
 * Add tags to a feature (with optional state, rating, comment)
 */
export const addTagsToFeature = async (featureId, tagAssignments) => {
  try {
    if (!tagAssignments || tagAssignments.length === 0) return;

    const inserts = tagAssignments.map(assignment => ({
      feature_id: featureId,
      tag_id: assignment.tag_id,
      state: assignment.state || null,
      rating: assignment.rating || null,
      comment: assignment.comment || null
    }));

    const { error } = await supabase
      .from('feature_tags')
      .upsert(inserts, { onConflict: 'feature_id,tag_id' });

    if (error) throw error;
  } catch (error) {
    logger.error('Error adding tags to feature:', error);
    throw error;
  }
};

/**
 * Update a feature tag assignment (state, rating, comment)
 */
export const updateFeatureTag = async (featureId, tagId, tagData) => {
  try {
    const { data, error } = await supabase
      .from('feature_tags')
      .update({
        state: tagData.state || null,
        rating: tagData.rating || null,
        comment: tagData.comment || null
      })
      .eq('feature_id', featureId)
      .eq('tag_id', tagId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error updating feature tag:', error);
    throw error;
  }
};

/**
 * Remove tags from a feature
 */
export const removeTagsFromFeature = async (featureId, tagIds) => {
  try {
    if (!tagIds || tagIds.length === 0) return;

    const { error } = await supabase
      .from('feature_tags')
      .delete()
      .eq('feature_id', featureId)
      .in('tag_id', tagIds);

    if (error) throw error;
  } catch (error) {
    logger.error('Error removing tags from feature:', error);
    throw error;
  }
};

/**
 * Set tags for a feature (replaces all existing tags)
 */
export const setFeatureTags = async (featureId, tagAssignments) => {
  try {
    // First, remove all existing tags
    const { error: deleteError } = await supabase
      .from('feature_tags')
      .delete()
      .eq('feature_id', featureId);

    if (deleteError) throw deleteError;

    // Then add the new tags with assignments
    if (tagAssignments && tagAssignments.length > 0) {
      await addTagsToFeature(featureId, tagAssignments);
    }
  } catch (error) {
    logger.error('Error setting feature tags:', error);
    throw error;
  }
};

/**
 * ============================================================================
 * COMPETITOR PROFILE MANAGEMENT FUNCTIONS
 * ============================================================================
 */

/**
 * Get all competitors for a feature group
 * Returns tags from the "competitors" dimension with their profiles
 */
export const getCompetitors = async (featureGroupId) => {
  try {
    // First, find the "competitors" dimension
    const { data: dimensions, error: dimError } = await supabase
      .from('dimensions')
      .select('id, name')
      .eq('feature_group_id', featureGroupId)
      .eq('name', 'competitors')
      .is('deleted_at', null)
      .single();

    if (dimError || !dimensions) {
      // Competitors dimension doesn't exist yet
      return [];
    }

    // Get all tags in the competitors dimension
    const { data: tags, error: tagsError } = await supabase
      .from('tags')
      .select(`
        id,
        name,
        dimension_id,
        feature_group_id
      `)
      .eq('dimension_id', dimensions.id)
      .order('name', { ascending: true });

    if (tagsError) throw tagsError;
    if (!tags || tags.length === 0) {
      return [];
    }

    // Get competitor profiles for these tags
    const tagIds = tags.map(t => t.id);
    const { data: profiles, error: profilesError } = await supabase
      .from('competitor_profiles')
      .select('*')
      .in('tag_id', tagIds);

    if (profilesError) throw profilesError;

    // Merge tags with profiles
    const profileMap = new Map((profiles || []).map(p => [p.tag_id, p]));
    return tags.map(tag => ({
      ...tag,
      profile: profileMap.get(tag.id) || null
    }));
  } catch (error) {
    logger.error('Error fetching competitors:', error);
    throw error;
  }
};

/**
 * Get competitor profile by tag_id
 */
export const getCompetitorByTagId = async (tagId) => {
  try {
    const { data, error } = await supabase
      .from('competitor_profiles')
      .select('*')
      .eq('tag_id', tagId)
      .single();

    if (error && error.code !== 'PGRST116') throw error; // PGRST116 is "not found"
    return data || null;
  } catch (error) {
    logger.error('Error fetching competitor profile:', error);
    throw error;
  }
};

/**
 * Create or update competitor profile
 */
export const upsertCompetitorProfile = async (tagId, featureGroupId, profileData) => {
  try {
    const { data, error } = await supabase
      .from('competitor_profiles')
      .upsert([{
        tag_id: tagId,
        feature_group_id: featureGroupId,
        company_name: profileData.company_name || '',
        company_url: profileData.company_url || null,
        company_summary: profileData.company_summary || null,
        pricing: profileData.pricing || null
      }], {
        onConflict: 'tag_id'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error upserting competitor profile:', error);
    throw error;
  }
};

/**
 * Delete competitor profile (and optionally the tag)
 */
export const deleteCompetitorProfile = async (tagId, deleteTag = false) => {
  try {
    // Delete the profile first
    const { error: profileError } = await supabase
      .from('competitor_profiles')
      .delete()
      .eq('tag_id', tagId);

    if (profileError) throw profileError;

    // Optionally delete the tag
    if (deleteTag) {
      const { error: tagError } = await supabase
        .from('tags')
        .delete()
        .eq('id', tagId);

      if (tagError) throw tagError;
    }
  } catch (error) {
    logger.error('Error deleting competitor profile:', error);
    throw error;
  }
};

/**
 * Calculate competitor score
 * Score = sum of (competitor_rating - needs_rating) for all features tagged with this competitor
 * Only counts features where both Needs and Competitor ratings exist
 */
export const calculateCompetitorScore = async (tagId, featureGroupId) => {
  try {
    // First, find the "competitors" and "Needs" dimensions
    const { data: dimensions, error: dimError } = await supabase
      .from('dimensions')
      .select('id, name')
      .eq('feature_group_id', featureGroupId)
      .in('name', ['competitors', 'Needs'])
      .is('deleted_at', null);

    if (dimError) throw dimError;

    const competitorsDim = dimensions.find(d => d.name === 'competitors');
    const needsDim = dimensions.find(d => d.name === 'Needs');

    if (!competitorsDim || !needsDim) {
      // If Needs dimension doesn't exist, return null to show message
      return null;
    }

    // Get all features tagged with this competitor tag
    const { data: competitorTags, error: compError } = await supabase
      .from('feature_tags')
      .select(`
        feature_id,
        rating as competitor_rating,
        tags!inner (
          id,
          dimension_id
        )
      `)
      .eq('tag_id', tagId)
      .not('rating', 'is', null);

    if (compError) throw compError;
    if (!competitorTags || competitorTags.length === 0) {
      return 0; // No features tagged, score is 0
    }

    // Get Needs ratings for the same features
    const featureIds = competitorTags.map(ct => ct.feature_id);
    const { data: needsTags, error: needsError } = await supabase
      .from('feature_tags')
      .select(`
        feature_id,
        rating as needs_rating,
        tags!inner (
          id,
          dimension_id
        )
      `)
      .in('feature_id', featureIds)
      .eq('tags.dimension_id', needsDim.id)
      .not('rating', 'is', null);

    if (needsError) throw needsError;

    // Create a map of feature_id -> needs_rating
    const needsMap = new Map((needsTags || []).map(nt => [nt.feature_id, nt.needs_rating]));

    // Calculate score: sum of (competitor_rating - needs_rating)
    let score = 0;
    competitorTags.forEach(ct => {
      const needsRating = needsMap.get(ct.feature_id);
      if (needsRating !== undefined) {
        score += (ct.competitor_rating - needsRating);
      }
    });

    return score;
  } catch (error) {
    logger.error('Error calculating competitor score:', error);
    throw error;
  }
};

/**
 * Get competitor statistics (feature count and score)
 */
export const getCompetitorStats = async (tagId, featureGroupId) => {
  try {
    // Get feature count
    const { count, error: countError } = await supabase
      .from('feature_tags')
      .select('*', { count: 'exact', head: true })
      .eq('tag_id', tagId);

    if (countError) throw countError;

    // Calculate score
    const score = await calculateCompetitorScore(tagId, featureGroupId);

    return {
      featureCount: count || 0,
      score: score
    };
  } catch (error) {
    logger.error('Error getting competitor stats:', error);
    throw error;
  }
};

/**
 * ============================================================================
 * DIMENSION MANAGEMENT FUNCTIONS
 * ============================================================================
 */

/**
 * Get all dimensions for a feature group (filtered by user permissions)
 */
export const getDimensions = async (featureGroupId) => {
  try {
    const { data, error } = await supabase
      .from('dimensions')
      .select('*')
      .eq('feature_group_id', featureGroupId)
      .is('deleted_at', null) // Only active dimensions
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error fetching dimensions:', error);
    throw error;
  }
};

/**
 * Get a single dimension by ID
 */
export const getDimensionById = async (dimensionId) => {
  try {
    const { data, error } = await supabase
      .from('dimensions')
      .select('*')
      .eq('id', dimensionId)
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error fetching dimension:', error);
    throw error;
  }
};

/**
 * Create a new dimension
 */
export const createDimension = async (dimensionData) => {
  try {
    const { data, error } = await supabase
      .from('dimensions')
      .insert([{
        feature_group_id: dimensionData.feature_group_id,
        name: dimensionData.name.trim(),
        admin_directions: dimensionData.admin_directions || null,
        user_directions: dimensionData.user_directions || null,
        dimension_type: dimensionData.dimension_type || 'BOOLEAN',
        visible_to_guest: dimensionData.visible_to_guest ?? false,
        visible_to_editor: dimensionData.visible_to_editor ?? true,
        visible_to_owner: dimensionData.visible_to_owner ?? true,
        editable_to_guest: dimensionData.editable_to_guest ?? false,
        editable_to_editor: dimensionData.editable_to_editor ?? true,
        editable_to_owner: dimensionData.editable_to_owner ?? true
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error creating dimension:', error);
    throw error;
  }
};

/**
 * Update an existing dimension
 */
export const updateDimension = async (dimensionId, dimensionData) => {
  try {
    const { data, error } = await supabase
      .from('dimensions')
      .update({
        name: dimensionData.name?.trim(),
        admin_directions: dimensionData.admin_directions || null,
        user_directions: dimensionData.user_directions || null,
        visible_to_guest: dimensionData.visible_to_guest,
        visible_to_editor: dimensionData.visible_to_editor,
        visible_to_owner: dimensionData.visible_to_owner,
        editable_to_guest: dimensionData.editable_to_guest,
        editable_to_editor: dimensionData.editable_to_editor,
        editable_to_owner: dimensionData.editable_to_owner,
        updated_at: new Date().toISOString()
      })
      .eq('id', dimensionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error updating dimension:', error);
    throw error;
  }
};

/**
 * Soft delete a dimension (sets deleted_at timestamp)
 */
export const deleteDimension = async (dimensionId) => {
  try {
    const { data, error } = await supabase
      .from('dimensions')
      .update({
        deleted_at: new Date().toISOString()
      })
      .eq('id', dimensionId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error deleting dimension:', error);
    throw error;
  }
};

/**
 * Purge a dimension (hard delete - permanently removes)
 */
export const purgeDimension = async (dimensionId) => {
  try {
    // First delete all tags in this dimension (cascade will handle feature_tags)
    const { error: tagsError } = await supabase
      .from('tags')
      .delete()
      .eq('dimension_id', dimensionId);

    if (tagsError) throw tagsError;

    // Then delete the dimension
    const { error } = await supabase
      .from('dimensions')
      .delete()
      .eq('id', dimensionId);

    if (error) throw error;
  } catch (error) {
    logger.error('Error purging dimension:', error);
    throw error;
  }
};

/**
 * Get dimension usage stats (percentage of features with tags)
 */
export const getDimensionUsageStats = async (dimensionId) => {
  try {
    const { data, error } = await supabase.rpc('get_dimension_tag_usage_stats', {
      p_dimension_id: dimensionId
    });

    if (error) throw error;
    return data?.[0] || { dimension_id: dimensionId, total_features: 0, features_with_tags: 0, percentage: 0 };
  } catch (error) {
    logger.error('Error fetching dimension usage stats:', error);
    throw error;
  }
};

/**
 * Export dimension to JSON (includes dimension config and tags)
 */
export const exportDimension = async (dimensionId) => {
  try {
    // Get dimension
    const dimension = await getDimensionById(dimensionId);
    
    // Get all tags in this dimension
    const { data: tags, error: tagsError } = await supabase
      .from('tags')
      .select('*')
      .eq('dimension_id', dimensionId)
      .order('name', { ascending: true });

    if (tagsError) throw tagsError;

    return {
      dimension: {
        name: dimension.name,
        admin_directions: dimension.admin_directions,
        user_directions: dimension.user_directions,
        dimension_type: dimension.dimension_type,
        visible_to_guest: dimension.visible_to_guest,
        visible_to_editor: dimension.visible_to_editor,
        visible_to_owner: dimension.visible_to_owner,
        editable_to_guest: dimension.editable_to_guest,
        editable_to_editor: dimension.editable_to_editor,
        editable_to_owner: dimension.editable_to_owner
      },
      tags: (tags || []).map(tag => ({
        name: tag.name,
        keywords: tag.keywords || []
      }))
    };
  } catch (error) {
    logger.error('Error exporting dimension:', error);
    throw error;
  }
};

/**
 * Import dimension from JSON (creates new dimension)
 */
export const importDimension = async (featureGroupId, dimensionJson) => {
  try {
    // Create the dimension
    const dimension = await createDimension({
      feature_group_id: featureGroupId,
      name: dimensionJson.dimension.name,
      admin_directions: dimensionJson.dimension.admin_directions,
      user_directions: dimensionJson.dimension.user_directions,
      dimension_type: dimensionJson.dimension.dimension_type,
      visible_to_guest: dimensionJson.dimension.visible_to_guest,
      visible_to_editor: dimensionJson.dimension.visible_to_editor,
      visible_to_owner: dimensionJson.dimension.visible_to_owner,
      editable_to_guest: dimensionJson.dimension.editable_to_guest,
      editable_to_editor: dimensionJson.dimension.editable_to_editor,
      editable_to_owner: dimensionJson.dimension.editable_to_owner
    });

    // Create tags if provided
    if (dimensionJson.tags && dimensionJson.tags.length > 0) {
      const tagInserts = dimensionJson.tags.map(tag => ({
        dimension_id: dimension.id,
        feature_group_id: featureGroupId,
        name: tag.name.trim(),
        keywords: tag.keywords || []
      }));

      const { error: tagsError } = await supabase
        .from('tags')
        .insert(tagInserts);

      if (tagsError) throw tagsError;
    }

    return dimension;
  } catch (error) {
    logger.error('Error importing dimension:', error);
    throw error;
  }
};

/**
 * Get users in a feature group
 */
export const getFeatureGroupUsers = async (featureGroupId) => {
  try {
    const { data, error } = await supabase.rpc('admin_list_feature_group_users', {
      p_feature_group_id: featureGroupId,
      p_limit: 500,
      p_offset: 0,
    });

    if (error) throw error;
    return (data || []).map(user => ({
      user_id: user.user_id,
      email: user.email,
      full_name: user.full_name,
      role: user.member_role || 'Guest',
      last_login: user.last_login,
      invite_status: user.invite_status,
      created_at: user.member_created_at, // created_at from feature_group_members
      last_visit: user.last_visit,
    }));
  } catch (error) {
    logger.error('Error fetching feature group users:', error);
    throw error;
  }
};

/**
 * Get user IDs that are already members of a feature group
 */
export const getFeatureGroupMemberIds = async (featureGroupId) => {
  try {
    const { data, error } = await supabase
      .from('feature_group_members')
      .select('user_id')
      .eq('feature_group_id', featureGroupId);

    if (error) throw error;
    return new Set((data || []).map(m => m.user_id));
  } catch (error) {
    logger.error('Error fetching feature group member IDs:', error);
    throw error;
  }
};

/**
 * Get companies for a feature group (companies used in user_companies for this feature group)
 */
export const getCompanies = async (featureGroupId) => {
  try {
    // Get companies directly from user_companies for this feature group
    const { data: userCompanies, error } = await supabase
      .from('user_companies')
      .select(`
        company_id,
        companies:company_id (
          id,
          name
        )
      `)
      .eq('feature_group_id', featureGroupId);

    if (error) throw error;
    
    // Extract unique companies
    const companyMap = new Map();
    (userCompanies || []).forEach(uc => {
      if (uc.companies && !companyMap.has(uc.companies.id)) {
        companyMap.set(uc.companies.id, {
          id: uc.companies.id,
          name: uc.companies.name
        });
      }
    });
    
    // Return sorted by name
    return Array.from(companyMap.values()).sort((a, b) => 
      a.name.localeCompare(b.name)
    );
  } catch (error) {
    logger.error('Error fetching companies:', error);
    throw error;
  }
};

/**
 * Get all companies for a feature group (for lookup)
 * @deprecated Use getAllCompaniesForFeatureGroup instead
 */
export const getAllCompanies = async (featureGroupId = null) => {
  try {
    if (featureGroupId) {
      return await getAllCompaniesForFeatureGroup(featureGroupId);
    }
    
    // If no featureGroupId provided, this should not be used
    // but keeping for backward compatibility
    const { data, error } = await supabase
      .from('companies')
      .select('*')
      .order('name', { ascending: true });

    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error fetching all companies:', error);
    throw error;
  }
};

/**
 * Create a new company
 */
export const createCompany = async (name, featureGroupId, votesPerPoll = 1, companyWeight = 1, companyType = null, companyAdminId = null) => {
  try {
    if (!featureGroupId) {
      throw new Error('featureGroupId is required to create a company');
    }
    
    // Use RPC function to create company, bypassing RLS issues
    const { data, error } = await supabase
      .rpc('create_company', {
        p_name: name.trim(),
        p_feature_group_id: featureGroupId,
        p_votes_per_poll: votesPerPoll || 1,
        p_company_weight: companyWeight || 1,
        p_company_type: companyType || null,
        p_company_admin_id: companyAdminId || null
      });

    if (error) throw error;
    // RPC returns an array, get the first element
    return data && data.length > 0 ? data[0] : data;
  } catch (error) {
    logger.error('Error creating company:', error);
    throw error;
  }
};

/**
 * Update a company
 */
export const updateCompany = async (companyId, name, votesPerPoll = 1, companyWeight = 1, companyType = null, companyAdminId = null) => {
  try {
    // Use RPC function to update company, bypassing RLS issues
    const { data, error } = await supabase
      .rpc('update_company', {
        p_company_id: companyId,
        p_name: name.trim(),
        p_votes_per_poll: votesPerPoll || 1,
        p_company_weight: companyWeight || 1,
        p_company_type: companyType || null,
        p_company_admin_id: companyAdminId || null
      });

    if (error) throw error;
    // RPC returns an array, get the first element
    return data && data.length > 0 ? data[0] : data;
  } catch (error) {
    logger.error('Error updating company:', error);
    throw error;
  }
};

/**
 * Delete a company
 */
export const deleteCompany = async (companyId) => {
  try {
    const { error } = await supabase
      .from('companies')
      .delete()
      .eq('id', companyId);

    if (error) throw error;
  } catch (error) {
    logger.error('Error deleting company:', error);
    throw error;
  }
};

/**
 * Public Buy-a-Feature form payload (no login). Returns null if slug is unknown or misconfigured.
 */
export const getBuyFeatureForm = async (slug) => {
  try {
    const { data, error } = await supabase.rpc('get_buy_feature_form', {
      p_slug: slug
    });
    if (error) throw error;
    return data || null;
  } catch (error) {
    logger.error('Error loading buy-a-feature form:', error);
    throw error;
  }
};

/**
 * Owner/superuser: set dimension + tag for the company's voting form, or pass nulls to disable.
 */
export const updateCompanyBuyFeatureSettings = async (companyId, dimensionId, tagId) => {
  try {
    const { data, error } = await supabase.rpc('update_company_buy_feature_settings', {
      p_company_id: companyId,
      p_dimension_id: dimensionId,
      p_tag_id: tagId
    });
    if (error) throw error;
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    logger.error('Error updating buy-a-feature settings:', error);
    throw error;
  }
};

/**
 * Get all companies for a feature group (all companies used in user_companies)
 */
export const getAllCompaniesForFeatureGroup = async (featureGroupId) => {
  try {
    // Use RPC function to get companies (includes newly created ones for Owners)
    const { data, error } = await supabase
      .rpc('get_companies_for_feature_group', {
        p_feature_group_id: featureGroupId
      });

    if (error) throw error;

    // Map to the expected format
    return (data || []).map(c => ({
      id: c.id,
      name: c.name,
      feature_group_id: c.feature_group_id,
      votes_per_poll: c.votes_per_poll || 1,
      company_weight: c.company_weight || 1,
      company_type: c.company_type,
      company_admin_id: c.company_admin_id,
      buy_feature_slug: c.buy_feature_slug || null,
      buy_feature_dimension_id: c.buy_feature_dimension_id || null,
      buy_feature_tag_id: c.buy_feature_tag_id || null
    })).sort((a, b) => a.name.localeCompare(b.name));
  } catch (error) {
    logger.error('Error fetching companies for feature group:', error);
    throw error;
  }
};

/**
 * Get user companies for a feature group
 */
export const getUserCompanies = async (featureGroupId) => {
  try {
    const { data, error } = await supabase
      .from('user_companies')
      .select(`
        id,
        user_id,
        company_id,
        feature_group_id,
        created_at,
        companies:company_id (
          id,
          name
        )
      `)
      .eq('feature_group_id', featureGroupId)
      .order('created_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(uc => ({
      id: uc.id,
      user_id: uc.user_id,
      company_id: uc.company_id,
      feature_group_id: uc.feature_group_id,
      company_name: uc.companies?.name || 'Unknown',
    }));
  } catch (error) {
    logger.error('Error fetching user companies:', error);
    throw error;
  }
};

/**
 * Get users for a specific company in a feature group
 */
export const getCompanyUsers = async (companyId, featureGroupId) => {
  try {
    // First, get user_companies records
    const { data: userCompanies, error: ucError } = await supabase
      .from('user_companies')
      .select('id, user_id, company_id, feature_group_id, created_at')
      .eq('company_id', companyId)
      .eq('feature_group_id', featureGroupId)
      .order('created_at', { ascending: true });

    if (ucError) throw ucError;
    if (!userCompanies || userCompanies.length === 0) {
      return [];
    }

    // Get user_ids
    const userIds = userCompanies.map(uc => uc.user_id);

    if (userIds.length === 0) {
      return [];
    }

    // Fetch user info (email and full_name) using RPC function
    const { data: userInfo, error: userInfoError } = await supabase.rpc('get_users_by_ids', {
      p_user_ids: userIds
    });

    if (userInfoError) {
      // Fallback: if RPC doesn't exist yet, try alternative approach
      logger.warn('get_users_by_ids RPC not available, using fallback:', userInfoError);
      
      // Fallback: get profiles and construct names
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('user_id, first_name, last_name')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      const userMap = new Map();
      (profiles || []).forEach(profile => {
        const fullName = profile.first_name || profile.last_name
          ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim()
          : 'Unknown';
        userMap.set(profile.user_id, {
          email: 'Unknown',
          full_name: fullName
        });
      });

      return userCompanies.map(uc => {
        const user = userMap.get(uc.user_id) || { email: 'Unknown', full_name: 'Unknown' };
        return {
          id: uc.id,
          user_id: uc.user_id,
          company_id: uc.company_id,
          feature_group_id: uc.feature_group_id,
          email: user.email,
          full_name: user.full_name,
          created_at: uc.created_at,
        };
      });
    }

    // Create a map of user_id to user info
    const userMap = new Map();
    (userInfo || []).forEach(user => {
      userMap.set(user.user_id, {
        email: user.email || 'Unknown',
        full_name: user.full_name || 'Unknown'
      });
    });

    // Combine the data
    return userCompanies.map(uc => {
      const user = userMap.get(uc.user_id) || { email: 'Unknown', full_name: 'Unknown' };
      return {
        id: uc.id,
        user_id: uc.user_id,
        company_id: uc.company_id,
        feature_group_id: uc.feature_group_id,
        email: user.email,
        full_name: user.full_name,
        created_at: uc.created_at,
      };
    });
  } catch (error) {
    logger.error('Error fetching company users:', error);
    throw error;
  }
};

/**
 * Add user to feature group with role
 */
export const addUserToFeatureGroup = async (userId, featureGroupId, role) => {
  try {
    const { data, error } = await supabase
      .from('feature_group_members')
      .upsert([{
        feature_group_id: featureGroupId,
        user_id: userId,
        role: role
      }], {
        onConflict: 'feature_group_id,user_id'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error adding user to feature group:', error);
    throw error;
  }
};

/**
 * Update user's role in a feature group
 */
export const updateUserRole = async (userId, featureGroupId, role) => {
  try {
    const { data, error } = await supabase
      .from('feature_group_members')
      .update({ role: role })
      .eq('feature_group_id', featureGroupId)
      .eq('user_id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error updating user role:', error);
    throw error;
  }
};

/**
 * Remove user from feature group
 */
export const removeUserFromFeatureGroup = async (userId, featureGroupId) => {
  try {
    const { error } = await supabase
      .from('feature_group_members')
      .delete()
      .eq('feature_group_id', featureGroupId)
      .eq('user_id', userId);

    if (error) throw error;
  } catch (error) {
    logger.error('Error removing user from feature group:', error);
    throw error;
  }
};

/**
 * Add user to company in feature group
 */
export const addUserToCompany = async (userId, companyId, featureGroupId) => {
  try {
    const { data, error } = await supabase
      .from('user_companies')
      .insert([{
        user_id: userId,
        company_id: companyId,
        feature_group_id: featureGroupId
      }])
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error adding user to company:', error);
    throw error;
  }
};

/**
 * Update user's company in feature group
 */
export const updateUserCompany = async (userId, companyId, featureGroupId) => {
  try {
    // First, check if user already has a company in this feature group
    const { data: existing, error: checkError } = await supabase
      .from('user_companies')
      .select('id')
      .eq('user_id', userId)
      .eq('feature_group_id', featureGroupId)
      .maybeSingle();

    // If error and it's not a "no rows" error, throw it
    if (checkError && checkError.code !== 'PGRST116') {
      throw checkError;
    }

    if (existing) {
      // Update existing record
      const { data, error } = await supabase
        .from('user_companies')
        .update({ company_id: companyId })
        .eq('id', existing.id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } else {
      // Create new record
      return await addUserToCompany(userId, companyId, featureGroupId);
    }
  } catch (error) {
    logger.error('Error updating user company:', error);
    throw error;
  }
};

/**
 * Remove user from company in feature group
 */
export const removeUserCompany = async (userId, featureGroupId) => {
  try {
    const { error } = await supabase
      .from('user_companies')
      .delete()
      .eq('user_id', userId)
      .eq('feature_group_id', featureGroupId);

    if (error) throw error;
  } catch (error) {
    logger.error('Error removing user company:', error);
    throw error;
  }
};

/**
 * Remove user from company in feature group
 */
export const removeUserFromCompany = async (userCompanyId) => {
  try {
    const { error } = await supabase
      .from('user_companies')
      .delete()
      .eq('id', userCompanyId);

    if (error) throw error;
  } catch (error) {
    logger.error('Error removing user from company:', error);
    throw error;
  }
};

/**
 * Search users by email (searches through feature group members)
 * Uses RPC function to search auth.users
 */
export const searchUsersByEmail = async (emailQuery) => {
  try {
    // Use RPC function to search users by email
    // Note: This only finds users who are already members of some feature group
    const { data, error } = await supabase.rpc('search_users_by_email', {
      p_email_query: emailQuery,
      p_limit: 10
    });

    if (error) throw error;
    
    return (data || []).map(user => ({
      id: user.id,
      email: user.email
    }));
  } catch (error) {
    logger.error('Error searching users:', error);
    throw error;
  }
};

/**
 * Get votes for a feature
 */
export const getFeatureVotes = async (featureId, featureGroupId) => {
  try {
    const { data, error } = await supabase
      .from('votes')
      .select(`
        id,
        feature_id,
        company_id,
        voted,
        companies:company_id (
          id,
          name
        )
      `)
      .eq('feature_id', featureId)
      .eq('feature_group_id', featureGroupId);

    if (error) throw error;
    return (data || []).map(vote => ({
      id: vote.id,
      feature_id: vote.feature_id,
      company_id: vote.company_id,
      voted: vote.voted,
      company_name: vote.companies?.name || 'Unknown'
    }));
  } catch (error) {
    logger.error('Error fetching feature votes:', error);
    throw error;
  }
};

/**
 * Set vote for a feature by company
 */
export const setFeatureVote = async (featureId, companyId, featureGroupId, voted) => {
  try {
    const { data, error } = await supabase
      .from('votes')
      .upsert([{
        feature_id: featureId,
        company_id: companyId,
        feature_group_id: featureGroupId,
        voted: voted
      }], {
        onConflict: 'feature_id,company_id,feature_group_id'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error setting feature vote:', error);
    throw error;
  }
};

/**
 * Get user's company for a feature group
 */
export const getUserCompany = async (userId, featureGroupId) => {
  try {
    const { data, error } = await supabase
      .from('user_companies')
      .select(`
        company_id,
        companies:company_id (
          id,
          name,
          votes_per_poll
        )
      `)
      .eq('user_id', userId)
      .eq('feature_group_id', featureGroupId)
      .maybeSingle();

    if (error) throw error;
    return data ? {
      company_id: data.company_id,
      company_name: data.companies?.name || 'Unknown',
      votes_per_poll: data.companies?.votes_per_poll || 1
    } : null;
  } catch (error) {
    logger.error('Error fetching user company:', error);
    throw error;
  }
};

/**
 * Get vote counts for multiple features (batch query)
 * Returns a Map of featureId -> {up: number, down: number, total: number, companyVotes: array}
 */
export const getFeatureVoteCounts = async (featureIds, featureGroupId) => {
  try {
    if (!featureIds || featureIds.length === 0) {
      return new Map();
    }

    const { data, error } = await supabase
      .from('votes')
      .select(`
        feature_id,
        company_id,
        voted,
        companies:company_id (
          id,
          name
        )
      `)
      .in('feature_id', featureIds)
      .eq('feature_group_id', featureGroupId);

    if (error) throw error;

    // Count votes per feature
    const counts = new Map();
    featureIds.forEach(featureId => {
      counts.set(featureId, {
        up: 0,
        down: 0,
        total: 0,
        companyVotes: []
      });
    });

    if (data) {
      data.forEach(vote => {
        const count = counts.get(vote.feature_id) || { up: 0, down: 0, total: 0, companyVotes: [] };
        if (vote.voted) {
          count.up += 1;
        } else {
          count.down += 1;
        }
        count.total += 1;
        count.companyVotes.push({
          company_id: vote.company_id,
          company_name: vote.companies?.name || 'Unknown',
          voted: vote.voted
        });
        counts.set(vote.feature_id, count);
      });
    }

    return counts;
  } catch (error) {
    logger.error('Error fetching feature vote counts:', error);
    throw error;
  }
};

/**
 * Toggle vote up/down for a feature
 * Returns the new vote state
 */
export const toggleFeatureVote = async (featureId, companyId, featureGroupId, currentVote) => {
  try {
    // Toggle: if currently voted, remove vote; if not voted, add vote
    const newVoteState = !currentVote;
    return await setFeatureVote(featureId, companyId, featureGroupId, newVoteState);
  } catch (error) {
    logger.error('Error toggling feature vote:', error);
    throw error;
  }
};

/**
 * Get ratings for a feature
 */
export const getFeatureRatings = async (featureId, featureGroupId) => {
  try {
    const { data, error } = await supabase
      .from('ratings')
      .select(`
        id,
        feature_id,
        company_id,
        rating,
        companies:company_id (
          id,
          name
        )
      `)
      .eq('feature_id', featureId)
      .eq('feature_group_id', featureGroupId);

    if (error) throw error;
    return (data || []).map(rating => ({
      id: rating.id,
      feature_id: rating.feature_id,
      company_id: rating.company_id,
      rating: rating.rating,
      company_name: rating.companies?.name || 'Unknown'
    }));
  } catch (error) {
    logger.error('Error fetching feature ratings:', error);
    throw error;
  }
};

/**
 * Set rating for a feature by company
 */
export const setFeatureRating = async (featureId, companyId, featureGroupId, rating) => {
  try {
    const { data, error } = await supabase
      .from('ratings')
      .upsert([{
        feature_id: featureId,
        company_id: companyId,
        feature_group_id: featureGroupId,
        rating: rating
      }], {
        onConflict: 'feature_id,company_id,feature_group_id'
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error setting feature rating:', error);
    throw error;
  }
};

/**
 * Admin: list feature group users with pagination
 */
export const adminListFeatureGroupUsers = async (featureGroupId, page = 0, limit = 100) => {
  try {
    const { data, error } = await supabase.rpc('admin_list_feature_group_users', {
      p_feature_group_id: featureGroupId,
      p_limit: limit,
      p_offset: page * limit,
    });

    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error listing admin feature group users:', error);
    throw error;
  }
};

/**
 * Resend invite email for a user
 */
export const resendUserInvite = async (email) => {
  try {
    const { data, error } = await supabase.auth.resend({
      type: 'signup',
      email,
    });

    if (error) throw error;
    return data;
  } catch (error) {
    logger.error('Error resending user invite:', error);
    throw error;
  }
};

/**
 * Get rating label
 */
export const getRatingLabel = (rating) => {
  const labels = {
    0: 'No Rating',
    1: 'Not Needed',
    2: 'Minimal Need',
    3: 'Moderate Need',
    4: 'Major Need',
    5: 'Must Have Need'
  };
  return labels[rating] || 'Unknown';
};

/**
 * Ensure a profile row exists for the current user
 */
const ensureUserProfileExists = async (user) => {
  const fullName = user.user_metadata?.full_name || user.user_metadata?.name || '';
  const [firstName, ...rest] = fullName.split(' ');
  const lastName = rest.join(' ');

  const profileData = {
    user_id: user.id,
    first_name: firstName || null,
    last_name: lastName || null,
    avatar_url: user.user_metadata?.avatar_url || null,
    linkedin_url: user.user_metadata?.linkedin || null,
  };

  const { data, error } = await supabase
    .from('user_profiles')
    .insert([profileData])
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Get or create the current user's profile
 */
export const getCurrentUserProfile = async () => {
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('user_id', user.id)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      return ensureUserProfileExists(user);
    }
    throw error;
  }

  return data;
};

/**
 * Update current user's profile
 */
export const updateUserProfile = async (profileUpdates) => {
  const {
    data: { user },
    error: authError,
  } = await supabase.auth.getUser();
  if (authError) throw authError;
  if (!user) throw new Error('Not authenticated');

  const { data, error } = await supabase
    .from('user_profiles')
    .update({
      ...profileUpdates,
      updated_at: new Date().toISOString(),
    })
    .eq('user_id', user.id)
    .select()
    .single();

  if (error) throw error;
  return data;
};

/**
 * Admin: list all feature groups with stats (superuser only)
 */
export const adminListAllFeatureGroups = async () => {
  try {
    const { data, error } = await supabase.rpc('admin_list_all_feature_groups');
    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error listing all feature groups:', error);
    throw error;
  }
};

/**
 * Admin: delete a user from all tables and Supabase Auth (superuser only)
 * Note: This uses a database function with SECURITY DEFINER to delete from auth.users
 */
export const adminDeleteUser = async (userId) => {
  try {
    // Delete from user_companies first (no cascade from auth.users)
    const { error: ucError } = await supabase
      .from('user_companies')
      .delete()
      .eq('user_id', userId);

    if (ucError) throw ucError;

    // Delete from feature_group_members (will cascade from auth.users, but doing explicitly for safety)
    const { error: fgmError } = await supabase
      .from('feature_group_members')
      .delete()
      .eq('user_id', userId);

    if (fgmError) throw fgmError;

    // Delete from user_profiles (will cascade from auth.users, but doing explicitly for safety)
    const { error: profileError } = await supabase
      .from('user_profiles')
      .delete()
      .eq('user_id', userId);

    if (profileError) throw profileError;

    // Delete from auth.users using database function with SECURITY DEFINER
    // This function checks for superuser role and then deletes from auth.users
    const { error: authError } = await supabase.rpc('admin_delete_user', {
      p_user_id: userId
    });

    if (authError) {
      // If the function fails, it might be because:
      // 1. User is not a superuser
      // 2. Function doesn't exist (migration not run)
      // 3. Permission issues
      throw new Error(`Failed to delete user from auth: ${authError.message}`);
    }
  } catch (error) {
    logger.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Admin: create a new feature group (superuser only)
 * Uses RPC function to bypass RLS
 */
export const adminCreateFeatureGroup = async (displayName) => {
  try {
    const { data, error } = await supabase
      .rpc('admin_create_feature_group', {
        p_display_name: displayName.trim()
      });

    if (error) throw error;
    // RPC returns an array, get the first element
    return data && data.length > 0 ? data[0] : data;
  } catch (error) {
    logger.error('Error creating feature group:', error);
    throw error;
  }
};

/**
 * Admin: list all users (superuser only)
 */
export const adminListAllUsers = async (page = 0, limit = 100) => {
  try {
    const { data, error } = await supabase.rpc('admin_list_all_users', {
      p_limit: limit,
      p_offset: page * limit,
    });
    if (error) throw error;
    return data || [];
  } catch (error) {
    logger.error('Error listing all users:', error);
    throw error;
  }
};
